import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Search, Plus, TriangleAlert } from 'lucide-react'
import * as Query from '../../../api/gql/query'
import MembersTable, { MemberRow } from './MembersTable'
import MembersTableSkeleton from './components/MembersTableSkeleton'
import MembersBulkBar from './components/MembersBulkBar'
import AddMemberDialog from './components/AddMemberDialog'
import FolderAccessDialog from './components/FolderAccessDialog'
import { useMemberActions } from './hooks/useMemberActions'
import { ModuleKey } from './lib/moduleMeta'

interface Props { projectId: string }

const MembersPage: React.FC<Props> = ({ projectId }) => {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useQuery(Query.PROJECT_USERS_WITH_ACCESS, {
    variables: { projectId }, skip: !projectId, fetchPolicy: 'cache-and-network',
  })
  const { data: rolesData } = useQuery(Query.PROJECT_ROLES)
  const roles = rolesData?.projectRoles ?? []
  const actions = useMemberActions(projectId, refetch)

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [overriddenOnly, setOverriddenOnly] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [addOpen, setAddOpen] = useState(false)
  const [folderMember, setFolderMember] = useState<{ id: string; name: string | null } | null>(null)

  const allRows: MemberRow[] = data?.projectUsersWithAccess ?? []

  const rows = useMemo(() => {
    const q = search.toLowerCase()
    return allRows.filter((r) => {
      if (roleFilter !== 'all' && r.projectRoleId !== roleFilter) return false
      if (overriddenOnly && !r.modules.some((m) => m.overridden)) return false
      if (!q) return true
      return `${r.user.name ?? ''} ${r.user.email ?? ''} ${r.companyName ?? ''}`.toLowerCase().includes(q)
    })
  }, [allRows, search, roleFilter, overriddenOnly])

  const existingUserIds = useMemo(() => new Set(allRows.map((r) => r.user.id)), [allRows])

  const toggle = (userId: string) => setSelected((prev) => {
    const next = new Set(prev); next.has(userId) ? next.delete(userId) : next.add(userId); return next
  })
  const toggleAll = () => setSelected((prev) =>
    rows.every((r) => prev.has(r.user.id)) ? new Set() : new Set(rows.map((r) => r.user.id)))

  const openFolderAccess = (userId: string) => {
    const row = allRows.find((r) => r.user.id === userId)
    if (row) setFolderMember({ id: row.user.id, name: row.user.name })
  }

  if (loading && !data) return <MembersTableSkeleton />

  // Without this the query error left `rows` empty and the page rendered a
  // perfectly healthy-looking "0 members" table — the symptom that hid the
  // project-admin lockout for months (Freelo #31448981).
  if (error && !data) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-8 text-center">
        <TriangleAlert className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm font-medium">{t('Members_load_failed', 'Could not load project members.')}</p>
        <p className="text-sm text-muted-foreground">{error.message}</p>
        <Button variant="outline" size="sm" className="mt-1" onClick={() => refetch()}>
          {t('Common_retry', 'Try again')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap py-1">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Members_search_placeholder')} className="h-9 w-[260px] pl-8 text-sm" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder={t('Members_filter_role')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all', 'All')}</SelectItem>
            {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.key ? t('role_' + r.key, r.name) : r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 h-9 text-sm text-muted-foreground cursor-pointer select-none">
          <Checkbox
            checked={overriddenOnly}
            onCheckedChange={(v) => setOverriddenOnly(v === true)}
          />
          {t('Members_filter_overridden')}
        </label>
        <Badge variant="secondary">{t('Members_count', { count: rows.length })}</Badge>
        <Button size="sm" className="h-9 ml-auto" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" />{t('Members_add')}
        </Button>
      </div>

      <MembersBulkBar
        selectedCount={selected.size}
        running={actions.running}
        onApply={(module, level) => actions.bulkApply([...selected], module, level)}
        onClearSelection={() => setSelected(new Set())}
      />

      <MembersTable
        rows={rows}
        selected={selected}
        pending={actions.running}
        roles={roles}
        onToggle={toggle}
        onToggleAll={toggleAll}
        onChangeRole={actions.changeRole}
        onSetLevel={(userId, module: ModuleKey, level) => actions.setModuleLevel(userId, module, level)}
        onSetActions={(userId, module: ModuleKey, moduleActions) => actions.setModuleActions(userId, module, moduleActions)}
        onReset={(userId, module: ModuleKey) => actions.resetModule(userId, module)}
        onRemove={actions.removeMember}
        onSetCompany={actions.setCompany}
        onFolderAccess={openFolderAccess}
      />

      <AddMemberDialog
        isOpen={addOpen}
        existingUserIds={existingUserIds}
        saving={actions.running}
        roles={roles}
        onClose={() => setAddOpen(false)}
        onConfirm={async (userId, roleId) => { setAddOpen(false); await actions.addMember(userId, roleId) }}
      />

      <FolderAccessDialog
        open={!!folderMember}
        projectId={projectId}
        member={folderMember}
        onClose={() => setFolderMember(null)}
      />
    </div>
  )
}

export default MembersPage
